/**
 * CommitList - Display recent commits with pagination.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, GitCommit, ExternalLink } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface Commit {
  sha: string;
  message: string;
  author: {
    name: string;
    date: string;
    avatarUrl?: string;
    login?: string;
  };
  url: string;
}

interface CommitListProps {
  commits: Commit[];
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

export function CommitList({ commits, isLoading, hasMore, onLoadMore }: CommitListProps) {
  const formatDate = (dateStr: string) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return dateStr;
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <GitCommit className="w-4 h-4" />
          Recent Commits ({commits.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[240px]">
          {commits.length === 0 && !isLoading ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No commits found
            </p>
          ) : (
            <div className="space-y-2 pr-4">
              {commits.map((commit) => (
                <a
                  key={commit.sha}
                  href={commit.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-2 rounded-md hover:bg-muted transition-colors group"
                >
                  <div className="flex items-start gap-2">
                    <code className="text-xs text-blue-600 dark:text-blue-400 font-mono flex-shrink-0">
                      {commit.sha.slice(0, 7)}
                    </code>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{commit.message.split("\n")[0]}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        {commit.author.login ? (
                          <span>@{commit.author.login}</span>
                        ) : (
                          <span>{commit.author.name}</span>
                        )}
                        <span>{formatDate(commit.author.date)}</span>
                      </div>
                    </div>
                    <ExternalLink className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  </div>
                </a>
              ))}
            </div>
          )}
        </ScrollArea>

        {hasMore && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onLoadMore}
            disabled={isLoading}
            className="w-full mt-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              "Load more"
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
